import React from 'react';
import { Briefcase, User } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const WhoIsRoyaFor = () => {
  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-green-100 rounded-full text-green-700 font-medium text-sm mb-6">
              For Everyone
            </div>
            <h1 className="heading-1 mb-6">Who is Roya for?</h1>
            <p className="text-xl text-gray-700">
              One financial ecosystem designed to serve both businesses and individuals across Saudi Arabia
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* For Merchants */}
              <Card className="product-card">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="p-3 rounded-xl"
                      style={{ background: 'linear-gradient(135deg, #1B9E4B 0%, #159642 100%)' }}
                    >
                      <Briefcase className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">For Merchants</h3>
                      <p className="text-lg text-gray-600" dir="rtl">للتجار</p>
                    </div>
                  </div>

                  <div className="space-y-4 mb-8">
                    {[
                      {
                        en: 'Grocery stores, mobile shops, restaurants and salons',
                        ar: 'بقالات جوالات مطاعم صالونات'
                      },
                      {
                        en: 'One POS + wallet + QR for all branches',
                        ar: 'بوينت واحد ومحفظة وكيار لكل الفروع'
                      },
                      {
                        en: 'Staff and cashier access control',
                        ar: 'وصول للكاشير وتفاصيل المبيعات'
                      },
                      {
                        en: 'End-of-day reports and insights',
                        ar: 'تقارير واضحة نهاية اليوم'
                      },
                    ].map((item, idx) => (
                      <div key={idx}>
                        <p className="text-gray-800 font-medium mb-1">• {item.en}</p>
                        <p className="text-gray-600 text-sm" dir="rtl">{item.ar}</p>
                      </div>
                    ))}
                  </div>

                  <Link to="/pos">
                    <Button className="btn-primary w-full">
                      Explore Merchant Solutions
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* For Individuals */}
              <Card className="product-card">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="p-3 rounded-xl"
                      style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}
                    >
                      <User className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">For Individuals</h3>
                      <p className="text-lg text-gray-600" dir="rtl">للشخص الواحد</p>
                    </div>
                  </div>

                  <div className="space-y-4 mb-8">
                    {[
                      {
                        en: 'One wallet for spending and daily life',
                        ar: 'محفظة وحده لمصاريفك اليومية'
                      },
                      {
                        en: 'Pay in stores, online and via QR',
                        ar: 'تدفع في المحلات والاونلاين وبالكيار'
                      },
                      {
                        en: 'Use the Roya Card with Apple Pay',
                        ar: 'وتستخدم بطاقة روية مع ابل باي'
                      },
                      {
                        en: 'Track every transaction in one place',
                        ar: 'وتشوف كل العمليات في مكان واحد'
                      },
                    ].map((item, idx) => (
                      <div key={idx}>
                        <p className="text-gray-800 font-medium mb-1">• {item.en}</p>
                        <p className="text-gray-600 text-sm" dir="rtl">{item.ar}</p>
                      </div>
                    ))}
                  </div>

                  <Link to="/wallet">
                    <Button className="btn-primary w-full">
                      Explore Personal Wallet
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 text-center mb-12">Why Choose Roya</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
                <div className="text-4xl font-bold mb-2" style={{ color: '#1B9E4B' }}>1</div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">One Platform</h3>
                <p className="text-gray-600">Business and personal finances in one app</p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
                <div className="text-4xl font-bold mb-2" style={{ color: '#1B9E4B' }}>100%</div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">Saudi Built</h3>
                <p className="text-gray-600">Made in Saudi Arabia for Saudi Arabia</p>
              </div>
              
              <div className="text-center p-6 bg-white rounded-2xl shadow-sm">
                <div className="text-4xl font-bold mb-2" style={{ color: '#1B9E4B' }}>24/7</div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">Always Available</h3>
                <p className="text-gray-600">Support and services whenever you need</p>
              </div>
            </div>

            <div className="text-center mt-12">
              <Link to="/contact">
                <Button className="btn-primary text-lg px-8 py-6">
                  Get Started with Roya
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhoIsRoyaFor;
